import { Chip, LinearProgress, List, ListItem, Stack, Tooltip, IconButton } from '@mui/material'
import { Link } from 'react-router'
import { yellow } from '@mui/material/colors'
import React, { ReactNode, useEffect, useState } from 'react'
import useAuth from '../../../hooks/useAuth'
import { Check, Science } from '@mui/icons-material'
import SourceApi from '../../../api/SourceApi'
import {
    getValidSourceTypes,
    isSingularIssueSourceType,
    isSingularWarrantSourceType,
} from '../../../utils/issues/sourceTypes'
import { IssueEntity } from 'types/IssueEntity'
import { PRInputEntity } from '@finance-lab/utils/types'
import { SourceType } from 'types/SourceType'
import { SourceEntity } from 'types/SourceEntity'
import { WarrantEntity } from 'types/WarrantEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { getPressReleaseLink } from '../../../utils/get-press-release-link'

const sourceApi = new SourceApi()

const getEventIsCompleted = (
    event: ConvertibleEntity | IssueEntity | WarrantEntity,
    eventType: 'convertible' | 'issue' | 'warrant',
) => {
    if (eventType === 'convertible') {
        return !!event.completed
    }
    if (eventType === 'issue') {
        return Boolean(event?.statusStage === 10 && event?.statusComplete)
    }
    if (eventType === 'warrant') {
        const warrant = event as WarrantEntity
        const isCompleted = warrant.subscriptionPeriods.every((period) => period.sharesSubscribed > 0)
        return isCompleted
    }
}

export const CompanyEventRow = ({
    companyId,
    event,
    eventType,
    sourceType,
    setSourceType,
    selectedPR,
    onCreateSource,
    onArchivePR,
    children,
}: {
    children: ReactNode
    companyId: string
    onArchivePR: (p: PRInputEntity) => Promise<void>
    sourceType: SourceType | null
    setSourceType: (s: SourceType | null) => void
    selectedPR: Pick<PRInputEntity, 'id'> | null
} & (
    | {
          eventType: 'convertible'
          event: ConvertibleEntity
          onCreateSource: (
              convertible: ConvertibleEntity,
              pr: PRInputEntity,
              sourceType: SourceType,
          ) => Promise<SourceEntity | null>
      }
    | {
          eventType: 'issue'
          event: IssueEntity
          onCreateSource: (
              issue: IssueEntity,
              pr: PRInputEntity,
              sourceType: SourceType,
          ) => Promise<SourceEntity | null>
      }
    | {
          eventType: 'warrant'
          event: WarrantEntity
          onCreateSource: (warrant: WarrantEntity, pr: PRInputEntity, sourceType: SourceType) => Promise<SourceEntity>
      }
)) => {
    const { isAuthenticated } = useAuth()
    const [existingSources, setExistingSources] = useState<SourceEntity[] | null>(null)

    const fetchSources = React.useCallback(async () => {
        if (eventType === 'issue') {
            const result = await sourceApi.findByIssueId(event.id)
            setExistingSources(result)
        }
        if (eventType === 'warrant') {
            const result = await sourceApi.findByWarrantId(event.id)

            console.log(`Existing sources for warrant ${event.name}`, result)
            setExistingSources(result)
        }
        if (eventType === 'convertible') {
            const result = await sourceApi.findByConvertibleId(event.id)

            console.log(`Existing sources for convertible ${event.name}`, result)
            setExistingSources(result)
        }
    }, [event.id, event.name, eventType])

    useEffect(() => {
        if (!isAuthenticated) return
        void fetchSources()
    }, [isAuthenticated, event, eventType, fetchSources])

    let associatedList = null

    if ((existingSources?.length ?? 0) > 0) {
        const prListItem = (source: SourceEntity) => (
            <ListItem key={source.id}>
                <Link
                    to={source.prId ? getPressReleaseLink(companyId, source.prId) : (source.sourceUrl ?? '')}
                    target='_blank'
                >
                    <Chip size='small' color='primary' variant='outlined' label={source.sourceType} />{' '}
                    {source.title || source.sourceUrl}
                </Link>
            </ListItem>
        )
        associatedList = (
            <List>
                {!existingSources && <LinearProgress />}
                {existingSources?.map((source) => prListItem(source))}
            </List>
        )
    }
    const onAssociate = async () => {
        if (!onCreateSource) {
            throw new Error('onCreateSource not implemented')
        }
        if (!sourceType) {
            alert('Välj källtyp först')
            return
        }
        if (!selectedPR) {
            console.log('ERROR: No selected PR')
            throw new Error('No selected PR')
        }
        // const associatedPR = { pr, sourceType }
        // check valid source type for issues and warrants
        if (
            getValidSourceTypes(
                eventType === 'issue' ? 'Issue' : eventType === 'convertible' ? 'Convertible' : 'Warrant',
                eventType === 'issue' ? event.type : null,
            ).includes(sourceType)
        ) {
            const isSingularType =
                eventType === 'issue' ? isSingularIssueSourceType(sourceType) : isSingularWarrantSourceType(sourceType)
            if (isSingularType && existingSources?.some((s) => s.sourceType === sourceType)) {
                alert(
                    `Källtypen ${sourceType} är redan kopplad till denna ${
                        eventType === 'issue' ? 'emission' : eventType === 'warrant' ? 'option' : 'konvertibel'
                    }`,
                )
                return
            }
            // if we reach this point, the source type is valid
        } else {
            alert(`Källtypen ${sourceType} är inte giltig för emissionstypen ${event.type}`)
            return
        }

        const source = await onCreateSource(event, selectedPR, sourceType)

        if (!source) {
            console.error('Failed to create source')
            return
        } else {
            console.log('Created source', source)
        }

        void fetchSources()

        if (setSourceType) setSourceType(null)
        if (onArchivePR) void onArchivePR(selectedPR)
    }

    const isEventCompleted = getEventIsCompleted(event, eventType)

    return (
        <tr>
            <td>
                <Stack spacing={2} key={event.id} sx={{ mb: associatedList ? 0 : 6 }}>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip
                            title={`Koppla PM till ${eventType === 'issue' ? 'emission' : eventType === 'warrant' ? 'option' : 'konvertibel'}`}
                        >
                            <IconButton
                                sx={{
                                    width: 30, // Adjust the width for your desired size
                                    height: 30, // Adjust the height for your desired size
                                    borderRadius: '50%', // Makes the button round
                                    backgroundColor: isEventCompleted ? 'success.main' : 'primary.main', // Optional: Set a background color for better visibility
                                    border: '2px solid', // Adds a border
                                    borderColor: isEventCompleted ? 'success.main' : 'primary.main', // Set border color with main variant
                                    color: 'white', // Set icon color for better contrast
                                    '&:hover': {
                                        backgroundColor: isEventCompleted ? 'success.dark' : 'primary.dark', // Darker color on hover
                                        borderColor: isEventCompleted ? 'success.dark' : 'primary.dark', // Darker border color on hover
                                    },
                                }}
                                onClick={() => void onAssociate()}
                            >
                                {isEventCompleted && <Check sx={{ fontSize: 22 }} />}
                                {!isEventCompleted && <Science sx={{ fontSize: 22 }} />}
                            </IconButton>
                        </Tooltip>
                        <>{children}</>
                    </Stack>
                    {associatedList}
                </Stack>
            </td>
        </tr>
    )
}
